import _ from 'lodash';

import { StoredImageInfo } from 'app/interfaces/Image';
import { ProcessingOptions } from 'app/interfaces/IImageProcessor';
import { ProcessingOptionsProvider } from 'app/hooks/useProcessingOptionsProvider';
import { StoredFilterDescription } from 'app/redux/processing/reducer';
import convertCropAnnotationToCropRectWithScale from 'app/utils/image/convertCropAnnotationToCropRectWithScale';

export type BuildProcessingOptionsOverrides = {
  processingOptionsProvider?: ProcessingOptionsProvider;
  processingFilters?: StoredFilterDescription[];
  /**
   * Pre-resolved base parameters (e.g. a fine-tuning `FilterDescription.parameters` or the
   * static `PROCESSING_OPTIONS[...]` entry). When provided the util skips the provider/filter
   * lookup. Loose/structural typing allows both `FullAlgorithmOptions` and the native-module
   * `ProcessingOptions` shapes.
   */
  baseParameters?: object;
  /** Overrides `image.image_metadata.processingType` for the provider lookup. */
  processingType?: string;
  /** DICOM/image-derived photometric interpretation; `StoredImageInfo` doesn't carry it. */
  photometricInterpretation?: string;
  /** Explicit grid value (e.g. acquisition study default); wins over the image metadata flag. */
  hasAntiScatteringGrid?: boolean;
  /**
   * Loaded *initial* image dimensions, in the raw-image coordinate space the `Crop` annotation
   * refers to. The util deliberately does NOT fall back to `displayableImage` (which is the
   * post-process image and can differ post-crop, and which `StoredImageInfo` doesn't expose).
   * When undefined and no `cropRect` override is given, `crop_rect` stays omitted.
   */
  imageDimensions?: { width: number; height: number };
  /** Pre-computed crop rect; short-circuits self-computation. */
  cropRect?: number[];
  isFinetuning?: boolean;
};

/**
 * Builds the full `ProcessingOptions` payload passed to the image-processing addon, by applying
 * image-specific normalization (crop rect, photometric interpretation, finetuning flag, anti-
 * scatter grid) on top of a caller-resolved base (provider lookup, custom filter, or a static
 * processing-options entry).
 *
 * Centralizes the per-pipeline ad-hoc option construction spread across
 * `processLoadingImage`, `processLoadedImage`, `processFineTunedImage`, `onNewAcquisitionImage`,
 * and `syncStudyFromStoreWithPACS`.
 *
 * Precedence:
 * - base parameters: `baseParameters` > `processingFilters` match > `processingOptionsProvider` lookup
 * - crop rect: `cropRect` override > computed from `image.annotations.Crop` + `viewport` + `imageDimensions` > omitted
 * - grid: `hasAntiScatteringGrid` override > `image.image_metadata.has_anti_scattering_grid` > `false`
 */
export const buildProcessingOptions = (
  image: Partial<StoredImageInfo>,
  overrides: BuildProcessingOptionsOverrides = {}
): ProcessingOptions => {
  const processingType = overrides.processingType ?? image.image_metadata?.processingType;

  const baseParameters =
    overrides.baseParameters ??
    (
      _.find(overrides.processingFilters, { id: processingType }) ??
      overrides.processingOptionsProvider?.getProcessingOptions(
        processingType,
        image.anatomicRegion
      )
    )?.parameters ??
    {};

  const processingOptions = _.cloneDeep(baseParameters) as ProcessingOptions;

  if (overrides.cropRect) {
    processingOptions.crop_rect = overrides.cropRect;
  } else if (image.annotations?.Crop && image.viewport && overrides.imageDimensions) {
    const cropRect = convertCropAnnotationToCropRectWithScale(
      image.annotations.Crop,
      image.viewport,
      overrides.imageDimensions
    );
    if (cropRect) processingOptions.crop_rect = cropRect;
  }

  if (overrides.photometricInterpretation !== undefined) {
    processingOptions.photometric_interpretation = overrides.photometricInterpretation;
  }
  if (overrides.isFinetuning !== undefined) {
    processingOptions.isFinetuning = overrides.isFinetuning;
  }

  processingOptions.has_anti_scattering_grid =
    overrides.hasAntiScatteringGrid ?? image.image_metadata?.has_anti_scattering_grid ?? false;

  return processingOptions;
};

export default buildProcessingOptions;
